import { Box, Button, Stack, Typography } from '@mui/material';
import { PhoneInput } from '@/global/components/PhoneInput';
import { EmailInput } from '@/global/components/EmailInput';
import React, { type Dispatch, type SetStateAction } from 'react';
import { HeaderInfo } from '@/modules/students/components/StudentsByIin/HeaderInfo';
import { StudentIntegrationField } from '@/modules/students/components/StudentsByIin/StudentIntegrationField';
import type { StudentInfoType } from '@/global/api/gen/ts/StudentInfoType';
import { BackLink } from '@/global/components/BackLink';
import { UserIcon } from '@/global/icons/UserIcon';
import KaspiLogo from '@/global/icons/KaspiLogo';
import { FreedomLogo } from '@/global/icons/FreedomLogo';
import type { TransactionCreateDtoPaymentSystemEnum } from '@/global/api/gen/ts/TransactionCreateDto';
import type { StudentInfoAisResponseDto } from '@/global/api/gen/ts/StudentInfoAisResponseDto';

interface Props {
    studentsByIIN: StudentInfoAisResponseDto;
    handleStartTransaction: (paymentMethod: TransactionCreateDtoPaymentSystemEnum) => void,
    setPhone: Dispatch<SetStateAction<{ phone: string, isValid: boolean }>>,
    setEmail: Dispatch<SetStateAction<{ email: string, isValid: boolean }>>,
    studentInfo: StudentInfoType,
    returnFirstStep: () => void,
    selectedTariff: 'tariff1' | 'tariff2' | 'tariff3' | 'tariff4' | null,
    setSelectedTariff: (tariff: 'tariff1' | 'tariff2' | 'tariff3' | 'tariff4' | null) => void
}

export function StudentsByIinStepStartTransaction({
                                                      studentsByIIN,
                                                      returnFirstStep,
                                                      handleStartTransaction,
                                                      studentInfo,
                                                      setPhone,
                                                      setEmail,
                                                      selectedTariff,
                                                      setSelectedTariff
                                                  }: Props) {

    const hasCertificate = Object.keys(studentsByIIN?.show_certificate || {})?.length > 0;

    return (
        <>
            <BackLink
                sx={{
                    alignSelf: 'flex-start',
                    fontSize: '18px'
                }}
                label={'Вернуться к вводу ИИН'}
                onClick={returnFirstStep}
            />
            <Stack
                gap={2}
                sx={{
                    width: {xs: '100%', md: '50%'},
                    backgroundColor: 'white',
                    padding: {xs: 1.5, md: 6},
                    borderRadius: '26px'
                }}
            >
                <Stack
                    flexDirection={'row'}
                    alignItems={'center'}
                    gap={2}
                >
                    <UserIcon size={61}/>
                    <Typography fontSize={'34px'}>
                        {`${studentInfo.last_name} ${studentInfo.first_name[0]}. ${studentInfo.patronymic?.[0] || ''}.`}
                    </Typography>
                </Stack>
                <HeaderInfo>
                    Данные курсанта
                </HeaderInfo>
                <Stack
                    flexDirection={'column'}
                    gap={1}
                >
                    <StudentIntegrationField
                        value={(
                            <>
                                {studentInfo.last_name}
                                {' '}
                                {studentInfo.first_name}
                                {' '}
                                {studentInfo.patronymic ?? ''}
                            </>
                        )}
                        label={'ФИО:'}
                    />
                    <StudentIntegrationField
                        label={'ИИН курсанта'}
                        value={studentInfo.iin}
                    />
                    {/*<StudentIntegrationField*/}
                    {/*    label={'Автошкола'}*/}
                    {/*    value={studentInfo.school_title}*/}
                    {/*/>*/}
                </Stack>
                <Typography
                    fontSize={19}
                    sx={{mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                >
                    {!selectedTariff ? 'Выберите тариф' : 'Выбранный тариф'}
                    {!!selectedTariff && <Button
                        variant="contained"
                        size="small"
                        sx={{height: 40}}
                        onClick={() => setSelectedTariff(null)}
                    >
                        Сменить тариф
                    </Button>}
                </Typography>
                <Stack gap={2}>
                    {(!hasCertificate && (!selectedTariff || selectedTariff === 'tariff1')) &&
                        <Stack sx={{background: '#ebf0fb', borderRadius: 2, p: 2}}>
                            <Typography
                                fontWeight={600}
                                fontSize={19}
                                sx={{mb: 1}}
                            >
                                Сведения об обучении
                            </Typography>
                            <Typography sx={{mb: 1.8}}>
                                Вы получите сведения об истории обучений в PDF формате
                            </Typography>
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{mt: 3}}
                            >
                                <Typography
                                    fontSize={23}
                                    fontWeight={300}
                                >
                                    2 490 ₸
                                </Typography>
                                {!selectedTariff && <Button
                                    variant="contained"
                                    size="small"
                                    sx={{height: 40}}
                                    onClick={() => setSelectedTariff('tariff1')}
                                >
                                    Выбрать
                                </Button>}
                            </Stack>
                        </Stack>}
                    {(hasCertificate && (!selectedTariff || selectedTariff === 'tariff4')) &&
                        <Stack sx={{background: '#ebf0fb', borderRadius: 2, p: 2}}>
                            <Typography
                                fontWeight={600}
                                fontSize={19}
                                sx={{mb: 1}}
                            >
                                Свидетельство
                            </Typography>
                            <Typography sx={{mb: 1.8}}>
                                Отправка электронного свидетельства на Email
                            </Typography>
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{mt: 3}}
                            >
                                <Typography
                                    fontSize={23}
                                    fontWeight={300}
                                >
                                    2 490 ₸
                                </Typography>
                                {!selectedTariff && <Button
                                    variant="contained"
                                    size="small"
                                    sx={{height: 40}}
                                    onClick={() => setSelectedTariff('tariff4')}
                                >
                                    Выбрать
                                </Button>}
                            </Stack>
                        </Stack>}
                    {(hasCertificate && (!selectedTariff || selectedTariff === 'tariff2')) &&
                        <Stack sx={{background: '#ebf0fb', borderRadius: 2, p: 2}}>
                            <Typography
                                fontWeight={600}
                                fontSize={19}
                                sx={{mb: 1}}
                            >
                                Модуль практика
                            </Typography>
                            <Typography sx={{mb: 1.8}}>
                                Отправка электронного свидетельства на Email
                            </Typography>
                            <Typography sx={{mb: 1}}>
                                + Курс по практическому экзамену (на 14 дней)
                            </Typography>
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{mt: 3}}
                            >
                                <Typography
                                    fontSize={23}
                                    fontWeight={300}
                                >
                                    4 990 ₸
                                </Typography>
                                {!selectedTariff && <Button
                                    variant="contained"
                                    size="small"
                                    sx={{height: 40}}
                                    onClick={() => setSelectedTariff('tariff2')}
                                >
                                    Выбрать
                                </Button>}
                            </Stack>
                        </Stack>}
                    {(hasCertificate && (!selectedTariff || selectedTariff === 'tariff3')) &&
                        <Stack sx={{background: '#ebf0fb', borderRadius: 2, p: 2}}>
                            <Typography
                                fontWeight={600}
                                fontSize={19}
                                sx={{mb: 1}}
                            >
                                Модуль теория + практика
                            </Typography>
                            <Typography sx={{mb: 1.8}}>
                                Отправка электронного свидетельства на Email
                            </Typography>
                            <Typography sx={{mb: 1}}>
                                + Курс по практическому экзамену (на 14 дней)
                            </Typography>
                            <Typography>
                                + Курс по ПДД Казахстана (на 14 дней)
                            </Typography>
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{mt: 3}}
                            >
                                <Typography
                                    fontSize={23}
                                    fontWeight={300}
                                >
                                    9 990 ₸
                                </Typography>
                                {!selectedTariff && <Button
                                    variant="contained"
                                    size="small"
                                    sx={{height: 40}}
                                    onClick={() => setSelectedTariff('tariff3')}
                                >
                                    Выбрать
                                </Button>}
                            </Stack>
                        </Stack>
                    }
                </Stack>
                {!!selectedTariff && (
                    <>
                        <Typography
                            alignSelf={'center'}
                            textAlign={'center'}
                        >
                            Все данные вы получите на этой странице после оплаты,
                            <br/>
                            {' '}
                            а также они будут продублированы на вашу почту
                        </Typography>
                        <Typography
                            alignSelf={'center'}
                            textAlign={'center'}
                            fontSize={'2.5rem'}
                            fontWeight={'lighter'}
                            sx={{
                                paddingBlock: 1,
                                paddingInline: 8,
                                borderRadius: 25,
                                background: 'linear-gradient(84.38deg, #CDD4F1 -5.65%, #FFFFFF 41.55%, #C7E7F4 61.13%, #EEF0FD 87.7%)'
                            }}
                        >
                            {selectedTariff === 'tariff1' ? '2 490' : selectedTariff === 'tariff2' ? '4 990' : selectedTariff === 'tariff3' ? '9 990' : '2 490'} тг.
                        </Typography>
                        <Stack gap={2}>
                            <Stack gap={1}>
                                <Typography>
                                    Электронная почта (куда придут данные):
                                </Typography>
                                <EmailInput
                                    placeholder={'example@demo.kz'}
                                    fullWidth
                                    onChange={(email, isValid) => setEmail({email, isValid})}
                                    sx={{
                                        borderRadius: '10px',
                                        '.MuiOutlinedInput-root': {
                                            backgroundColor: '#ebf0fb'
                                        },
                                        '.MuiOutlinedInput-input': {
                                            color: '#05304F',
                                            padding: '18px',
                                            fontSize: '16px',
                                            lineHeight: '24px'
                                        },
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'transparent'
                                        }
                                    }}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <Typography>
                                    Номер телефона:
                                </Typography>
                                <PhoneInput
                                    placeholder={'+7 (777) 777-77-77'}
                                    fullWidth
                                    onChange={(phone, isValid) => setPhone({phone, isValid})}
                                    sx={{
                                        borderRadius: '10px',
                                        '.MuiOutlinedInput-root': {
                                            backgroundColor: '#ebf0fb'
                                        },
                                        '.MuiOutlinedInput-input': {
                                            color: '#05304F',
                                            padding: '18px',
                                            fontSize: '16px',
                                            lineHeight: '24px'
                                        },
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'transparent'
                                        }
                                    }}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <Typography
                                    fontSize={'22px'}
                                    fontWeight={'bold'}
                                >
                                    Платежная система:
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)'},
                                        gap: 2
                                    }}
                                >
                                    <Button
                                        onClick={() => handleStartTransaction('kaspi')}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            cursor: 'pointer',
                                            borderRadius: '12px',
                                            border: '4px solid transparent',
                                            padding: 4,
                                            backgroundColor: 'rgb(235, 45, 41)',
                                            transition: 'all 0.5s',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgb(195,38,34)'
                                            }
                                        }}
                                    >
                                        <KaspiLogo/>
                                    </Button>
                                    <Button
                                        onClick={() => handleStartTransaction('freedom')}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            borderRadius: '12px',
                                            height: '100%',
                                            border: '4px solid transparent',
                                            backgroundColor: 'rgba(100,255,162)',
                                            transition: 'all 0.5s',
                                            padding: 4,
                                            '&:hover': {
                                                backgroundColor: 'rgba(1,142,57)'
                                            }
                                        }}
                                    >
                                        <FreedomLogo/>
                                    </Button>
                                </Box>
                            </Stack>
                        </Stack>
                    </>
                )}
            </Stack>
        </>
    );
}
